import { prefersReducedMotion, yieldToMainThread } from '@theme/utilities';

/**
 * Shade below-the-fold scroll-reveal animation + ticker marquee.
 *
 * Reveal: mirrors the hero boot animation exactly: translateY 12px + opacity,
 * 0.45s ease-out, ~70ms stagger per child, IntersectionObserver per section,
 * prefersReducedMotion gate. Transform/opacity only so CLS is zero and product
 * image lazy-loading is unaffected.
 *
 * Targets: product cards, collection cards, and h2/h3/h4 text blocks (as before),
 * plus the trust strip items, the footer JOIN SHADE copy, and the newsletter
 * form. Trust items and the footer heading use non-h2/h3/h4 presets, so they are
 * added by explicit selectors rather than the .text-block.hN query.
 *
 * Ticker: a seamless terminal-style marquee. The single ticker text block is
 * duplicated (the copy is aria-hidden) inside an injected .shade-ticker-track
 * belt; CSS animates the belt left by exactly one copy-width and loops. Built
 * once at attach time; the motion itself lives entirely in a no-preference CSS
 * media query, so even if this ran under reduced motion no movement would occur.
 *
 * Hero avoidance: on connectedCallback the element snapshots window.innerHeight
 * and skips any reveal target whose getBoundingClientRect().top is below that
 * threshold at first paint. The hero, ticker strip, and tagline all sit within
 * the first viewport and are never revealed by the observer. The tagline has its
 * own CSS reveal hooked to jumbo-text's .ready class instead.
 *
 * Theme-editor safe: re-scans on shopify:section:load, disconnects any prior
 * observers, is idempotent (data-shade-reveal / data-shade-ticker guard
 * double-init), and the ticker injection no-ops if the track already exists.
 */
class ShadeReveal extends HTMLElement {
  /** @type {IntersectionObserver[]} */
  #observers = [];

  /** @type {ResizeObserver[]} */
  #resizeObservers = [];

  /** @type {{el: EventTarget, type: string, fn: EventListener}[]} */
  #scrollListeners = [];

  /** @type {number} Viewport height captured at attach time for hero skip. */
  #foldY = 0;

  connectedCallback() {
    // Non-motion DOM setup (ticker, trust strip, sticky CTA, carousel progress
    // bars) must run for everyone, including prefers-reduced-motion users; only
    // the entry reveals are motion and are gated inside #attach. The earlier
    // blanket early-return here stripped functional, motion-neutral features
    // (including the conversion CTA) from reduced-motion users.
    this.#attach();
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
  }

  disconnectedCallback() {
    this.#teardown();
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
  }

  /** Re-scan when any section reloads in the theme editor. */
  #onSectionLoad = () => {
    this.#teardown();
    this.#attach();
  };

  #teardown() {
    for (const obs of this.#observers) obs.disconnect();
    this.#observers = [];
    for (const ro of this.#resizeObservers) ro.disconnect();
    this.#resizeObservers = [];
    for (const { el, type, fn } of this.#scrollListeners) el.removeEventListener(type, fn);
    this.#scrollListeners = [];
    // Remove the body-appended sticky CTA so a removed hero section does not
    // leave an orphaned, permanently-visible bar; #initStickyCta recreates it
    // if the hero is still present.
    document.querySelector('.shade-sticky-cta')?.remove();
    // Same for the body-level scroll-progress bar, so #initScrollProgress
    // recreates it and re-binds its scroll listener on re-attach.
    document.querySelector('.shade-scroll-progress')?.remove();
    // The mobile carousel scrollbars, desktop gallery frame counters, and hero
    // scroll cue are injected with one-shot init guards (data-shade-scroll /
    // data-shade-scrub / element presence). Their listeners were just removed
    // above, so we must also remove the injected nodes AND clear the guards;
    // otherwise #initMobileCarousels / #initGalleryScrub / #initScrollCue bail
    // on the stale guard at re-attach and the progress bars, gallery scrub, and
    // scroll cue stay frozen with no listener for the rest of the session.
    document.querySelectorAll('.shade-scrollbar').forEach((el) => el.remove());
    document.querySelectorAll('.shade-frame-counter').forEach((el) => el.remove());
    document.querySelectorAll('[data-shade-scroll]').forEach((el) => el.removeAttribute('data-shade-scroll'));
    document.querySelectorAll('[data-shade-scrub]').forEach((el) => el.removeAttribute('data-shade-scrub'));
    document.querySelector('.shade-scroll-cue')?.remove();
  }

  /**
   * Build the ticker belt, then find all below-the-fold reveal groups and set up
   * one observer per group. A "group" is the closest shopify-section wrapper;
   * cards stagger within it.
   */
  #attach() {
    // Ticker marquee. Idempotent and safe on any page (no-op if absent).
    this.#initTicker();

    // Announcement bar rolling ticker (top of every page). Idempotent.
    this.#initAnnouncementBar();

    // Tagline vertical roll between its two statements. Idempotent.
    this.#initTaglineRoll();

    // Trust strip marquee on mobile. Mirrors ticker; mobile-only, idempotent.
    this.#initTrustMarquee();

    // Mobile sticky Shop CTA. Mobile-only, idempotent, perf-safe.
    this.#initStickyCta();

    // Mobile carousels: custom scroll-progress bar on the product/collection
    // carousels + the field gallery, plus active-slide emphasis on the gallery.
    // Mobile-only, idempotent, perf-safe (passive scroll + rAF).
    this.#initMobileCarousels();

    // Global top scroll-progress hairline. The bar grows on scroll (motion), so
    // it is gated behind reduced-motion inside the method (it is called here,
    // before the entry-reveal gate, only because it is otherwise body-level).
    this.#initScrollProgress();

    // Everything below is the entry-reveal motion layer; skip it under
    // reduced-motion so those users keep a static, fully-visible page (the
    // non-motion features above have already been set up).
    if (prefersReducedMotion()) return;

    // Desktop "tested in the field" contact-sheet scrub: the active frame
    // advances left-to-right as the section passes through the viewport.
    this.#initGalleryScrub();

    // Hero scroll cue: a blueprint tick + "SCROLL" that fades once past the fold.
    this.#initScrollCue();

    // Snapshot the fold position. Elements whose top is above this line at
    // first paint are inside the hero/ticker/tagline zone and are skipped.
    this.#foldY = window.innerHeight;

    // Gather all raw targets across the page. The first three selectors are the
    // original below-fold set; the last three add the trust strip, the footer
    // JOIN SHADE copy (h5 preset, missed by the .hN query), and the newsletter
    // form. All inherit the existing [data-shade-reveal] CSS with no new rules.
    const rawTargets = /** @type {HTMLElement[]} */ ([
      ...document.querySelectorAll(
        [
          'product-card.product-card',
          '.collection-card',
          '.text-block.h4',
          '.text-block.h3',
          '.text-block.h2',
          "[id$='__trust'] .text-block",
          "[id$='__benefits'] .media-with-content__content .group-block",
          "[id$='__banner'] .button-secondary",
          "[id$='__reviews'] .text-block",
          "[id$='__footer_main'] .text-block",
          "[id$='__footer_main'] .email-signup-block",
          "[id$='__footer_main'] .logo-block",
        ].join(', ')
      ),
    ]);

    // Group targets by their nearest .shopify-section ancestor. Each section
    // gets its own IntersectionObserver so stagger indices reset per section.
    /** @type {Map<Element, HTMLElement[]>} */
    const groups = new Map();

    for (const el of rawTargets) {
      // No fold-skip by rect: at module-load the images have not loaded yet, so
      // mid-page sections measure as compressed up past the fold and were being
      // wrongly skipped (collections, benefits never animated). None of the
      // targets live in the hero/ticker/tagline zone (those have their own
      // animations), so observing them all is safe; the IntersectionObserver
      // reveals anything already in view on its first tick.

      // Skip if already initialised (idempotent on section editor reloads that
      // only refresh one section).
      if (el.dataset.shadeReveal === 'active') continue;

      // Skip trust items that the mobile marquee has cloned into its belt: the
      // marquee provides their motion, and an opacity:0 idle state would hide a
      // scrolling label. Desktop has no belt, so trust items still reveal there.
      if (el.closest('.shade-trust-track')) continue;

      // Skip display:none elements (e.g. the hidden mobile-carousel card clones
      // on desktop, and the hidden desktop grid on mobile). offsetParent is null
      // only when an ancestor is display:none here. This keeps the group's
      // reveal sentinel a VISIBLE element, so the group always intersects and
      // never leaves the visible cards stuck at opacity:0.
      if (!el.offsetParent) continue;

      const section = el.closest('.shopify-section') || document.body;
      if (!groups.has(section)) groups.set(section, []);
      groups.get(section).push(el);
    }

    for (const [, targets] of groups) {
      this.#observeGroup(targets);
    }
  }

  /**
   * Build the seamless ticker marquee. Duplicates the ticker's rendered content
   * into a .shade-ticker-track belt (the second copy aria-hidden) and tags it
   * data-shade-ticker="running" for the CSS keyframe to drive. Idempotent: bails
   * if the track already exists. Safe on non-ticker pages (querySelector null).
   */
  #initTicker() {
    const host = document.querySelector("[id$='__ticker'] .section-content-wrapper");
    if (!host) return;

    // Avoid double-init on theme-editor reloads.
    if (host.querySelector('.shade-ticker-track')) return;

    const originalContent = host.innerHTML;
    if (!originalContent.trim()) return;

    // Wrap the scarcity count ("47 Units remaining") so the CSS can blink it on
    // a clock cadence. Resilient: matches any "<n> unit(s) remaining" so an
    // editor copy change still works, and if the phrase is absent nothing is
    // wrapped and the ticker is unaffected. Skip if already wrapped.
    const unitContent = originalContent.includes('shade-ticker-units')
      ? originalContent
      : originalContent.replace(
          /(\d+\s*units?\s+remaining)/i,
          '<span class="shade-ticker-units">$1</span>'
        );

    // The CSS scrolls the belt by exactly -50% (one half-width) and loops, so
    // the track is two identical halves, and each half must be at least as wide
    // as the viewport or a gap (empty bar) shows mid-scroll. Measure ONE real
    // unit's rendered width (the HOST is the full-bleed bar, NOT the unit, so the
    // old host-width measure collapsed repeats to 2 and left an empty section),
    // then repeat it enough to overflow the viewport.
    const track = document.createElement('div');
    track.className = 'shade-ticker-track';
    track.dataset.shadeTicker = 'running';
    host.innerHTML = '';
    host.appendChild(track);

    track.innerHTML = unitContent;
    const unitWidth = track.getBoundingClientRect().width || window.innerWidth;
    const repeatsPerHalf = Math.min(Math.max(Math.ceil(window.innerWidth / Math.max(unitWidth, 1)) + 1, 2), 12);
    const half = unitContent.repeat(repeatsPerHalf);
    track.innerHTML = half + half;

    // Only the first repeat stays in the accessibility tree; the rest are
    // decorative duplicates.
    const children = Array.from(track.children);
    for (let i = 1; i < children.length; i += 1) {
      children[i].setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Turn the top announcement bar into a seamless rolling ticker. The bar is a
   * native announcement-bar-component slider with a single slide; we leave the
   * slider alone and only rebuild the slide's text into a scrolling belt. The
   * single message string carries both notices (Free Shipping / Returns); we
   * repeat it with a bullet separator so it reads continuously, then duplicate
   * the belt for a seamless -50% loop (same machinery as #initTicker). The CSS
   * keyframe lives inside a prefers-reduced-motion: no-preference query, so
   * reduced-motion users see a static, readable belt. Idempotent.
   */
  #initAnnouncementBar() {
    const message = document.querySelector('.announcement-bar__text');
    if (!message) return;
    if (message.dataset.shadeAnn === 'rolling') return;

    // The single message string carries both notices joined by a bullet; split
    // them into separate lines so the bar shows ONE at a time and rolls down to
    // the next, instead of scrolling continuously across.
    const parts = (message.textContent || '')
      .split('•')
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length < 2) return; // need at least two messages to rotate

    message.dataset.shadeAnn = 'rolling';
    message.classList.add('shade-ann-roll');

    // Belt = each message on its own line + a clone of the first appended, so the
    // downward roll loops back to the start with no visible jump (the last line
    // renders the same text as the first). CSS drives the stepped roll.
    const lines = parts.concat(parts[0]);
    const belt = document.createElement('span');
    belt.className = 'shade-ann-belt';
    lines.forEach((lineText, i) => {
      const line = document.createElement('span');
      line.className = 'shade-ann-line';
      line.textContent = lineText;
      // Only the appended clone is decorative; the real messages stay readable.
      if (i === lines.length - 1) line.setAttribute('aria-hidden', 'true');
      belt.appendChild(line);
    });
    message.replaceChildren(belt);
  }

  /**
   * Vertical roll between the tagline's two statements (same motion as the
   * announcement bar). The block renders the statements as .shade-tagline-line
   * spans; we move them into a belt, append a clone of the first for a seamless
   * downward loop, measure the tallest (statements wrap to different line counts)
   * and pin every line + the viewport to that height, then expose it as
   * --tagline-roll-h for the CSS keyframe. Re-measures on width change (guarded
   * so setting the height does not retrigger the observer). Idempotent: reuses an
   * existing belt. Animation is CSS-gated by prefers-reduced-motion.
   */
  #initTaglineRoll() {
    const root = document.querySelector("[id$='__tagline'] .shade-tagline-roll");
    if (!root) return;

    let belt = root.querySelector('.shade-tagline-belt');
    if (!belt) {
      const lines = Array.from(root.querySelectorAll(':scope > .shade-tagline-line'));
      if (lines.length < 2) return;
      belt = document.createElement('span');
      belt.className = 'shade-tagline-belt';
      const clone = lines[0].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      lines.forEach((line) => belt.appendChild(line));
      belt.appendChild(clone);
      root.appendChild(belt);
    }

    const section = root.closest("[id$='__tagline']");
    let lastWidth = -1;
    const measure = () => {
      const width = Math.round(root.getBoundingClientRect().width);
      if (width === lastWidth) return; // only re-measure on width change (setting height must not loop)
      lastWidth = width;
      const items = Array.from(belt.children);
      items.forEach((line) => {
        line.style.height = 'auto';
      });
      let tallest = 0;
      items.forEach((line) => {
        tallest = Math.max(tallest, line.getBoundingClientRect().height);
      });
      tallest = Math.ceil(tallest);
      if (tallest <= 0) return;
      items.forEach((line) => {
        line.style.height = `${tallest}px`;
      });
      root.style.height = `${tallest}px`;
      if (section) section.style.setProperty('--tagline-roll-h', `${tallest}px`);
    };

    measure();
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => measure());
      ro.observe(root);
      this.#resizeObservers.push(ro);
    }
    if (section) section.setAttribute('data-tagline-rolling', '');
  }

  /**
   * Build the trust-strip marquee belt on mobile. Only runs when
   * matchMedia <=749px. Mirrors #initTicker exactly: clones each .text-block
   * label, inserts a bullet separator between items, doubles the set for a
   * seamless -50% loop. Idempotent: bails if .shade-trust-track already
   * exists. Never touches desktop layout (guard is inside the mobile media
   * query check). Does not depend on prefersReducedMotion() because the
   * animation itself is CSS-gated by prefers-reduced-motion; the DOM injection
   * is harmless either way (a static belt is still readable).
   */
  #initTrustMarquee() {
    const host = document.querySelector("[id$='__trust'] .section-content-wrapper");
    if (!host) return;

    // Idempotent: bail if already injected.
    if (host.querySelector('.shade-trust-track')) return;

    // Only the ORIGINAL direct-child text blocks (not future clones).
    const items = Array.from(host.querySelectorAll(':scope > .text-block'));
    if (items.length === 0) return;

    // Build one "unit": items separated by a bullet separator.
    // Each item is a shallow clone of the original element so all classes,
    // text, and inline styles are preserved.
    const buildUnit = (ariaHidden) => {
      const frag = document.createDocumentFragment();
      items.forEach((item, i) => {
        const clone = item.cloneNode(true);
        if (ariaHidden) clone.setAttribute('aria-hidden', 'true');
        frag.appendChild(clone);

        // Slash separator after every item (matches the desktop static row).
        const sep = document.createElement('span');
        sep.className = 'shade-trust-separator';
        sep.setAttribute('aria-hidden', 'true');
        sep.textContent = '/';
        frag.appendChild(sep);
      });
      return frag;
    };

    // Build the belt: measure one unit, then repeat it enough to overflow the
    // viewport so the -50% CSS loop is seamless on wide desktops (one unit fills
    // a phone but leaves a gap on a 1920px screen). The track is two equal
    // halves of `repeats` units each; CSS translates it -50% (one half) and
    // loops. Only the first unit stays in the accessibility tree.
    const track = document.createElement('div');
    track.className = 'shade-trust-track';
    track.dataset.shadeTrust = 'running';

    // Append (do NOT wipe the originals): the static slash row stays as the
    // reduced-motion / no-JS fallback, and CSS shows the belt otherwise.
    host.appendChild(track);

    track.appendChild(buildUnit(false));
    const unitWidth = track.getBoundingClientRect().width || window.innerWidth;
    const repeats = Math.min(Math.max(Math.ceil(window.innerWidth / Math.max(unitWidth, 1)) + 1, 2), 12);
    track.replaceChildren();
    for (let i = 0; i < repeats; i += 1) track.appendChild(buildUnit(i !== 0));
    for (let i = 0; i < repeats; i += 1) track.appendChild(buildUnit(true));
  }

  /**
   * Build the mobile sticky Shop CTA. A fixed bottom bar appears once the hero
   * CTA scrolls out of view and hides again when the footer is reached, so the
   * primary action is always one tap away below the fold. Mobile only (the CSS
   * keeps it display-able only under 749px). Idempotent, theme-editor safe, and
   * does not depend on motion: the slide animation is CSS-gated by
   * prefers-reduced-motion, the show/hide toggle is class-only.
   */
  #initStickyCta() {
    if (!('IntersectionObserver' in window)) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const heroCta = document.querySelector('.hero .button-secondary');
    if (!heroCta) return;

    const href = heroCta.getAttribute('href') || '/collections/all';
    const footer =
      document.querySelector("[id$='__footer_main']") ||
      document.querySelector('.shopify-section-group-footer-group') ||
      document.querySelector('footer');

    let bar = document.querySelector('.shade-sticky-cta');
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'shade-sticky-cta';
      const link = document.createElement('a');
      link.className = 'shade-sticky-cta__link';
      link.href = href;
      // Mirror the live hero CTA label so the campaign name never goes stale and
      // never needs a JS redeploy per drop. The trailing arrow is a decorative
      // CSS ::after (aria-hidden), so it stays out of the accessible name.
      link.textContent = heroCta.textContent?.trim() || 'Shop now';
      bar.appendChild(link);
      document.body.appendChild(bar);
    }

    const ctaObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        bar.classList.toggle('shade-sticky-cta--visible', !entry.isIntersecting);
      },
      { threshold: 0 }
    );
    ctaObserver.observe(heroCta);
    this.#observers.push(ctaObserver);

    if (footer) {
      const footerObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          if (!entry) return;
          bar.classList.toggle('shade-sticky-cta--at-footer', entry.isIntersecting);
        },
        { threshold: 0 }
      );
      footerObserver.observe(footer);
      this.#observers.push(footerObserver);
    }
  }

  /**
   * Build a custom horizontal scroll-progress bar for each mobile carousel
   * (the product/collection resource-list carousels and the field gallery),
   * and add active-slide emphasis to the gallery. Mobile only.
   *
   * Why custom, not native: iOS/Android touch scrollers use transient overlay
   * scrollbars, so a styled ::-webkit-scrollbar never persists on the phones
   * this matters for. A thin injected indicator reads as on-brand, signals the
   * row is swipeable, and reflects scroll position. Transform/opacity only,
   * passive scroll listener, rAF-throttled: no layout thrash, no CLS.
   *
   * Idempotent (data-shade-scroll guard), theme-editor safe (re-scans on
   * section load; old bars are inert duplicates removed on teardown only if
   * present), reduced-motion safe (the bar is static; only the optional thumb
   * transition lives in a no-preference media query in CSS).
   */
  #initMobileCarousels() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const configs = [
      { scroller: "[id$='__new_in'] .resource-list__carousel slideshow-slides", mount: "[id$='__new_in'] .resource-list__carousel" },
      { scroller: "[id$='__bestsellers'] .resource-list__carousel slideshow-slides", mount: "[id$='__bestsellers'] .resource-list__carousel" },
      { scroller: "[id$='__collections'] .resource-list__carousel slideshow-slides", mount: "[id$='__collections'] .resource-list__carousel" },
      { scroller: "[id$='__gallery'] .group-block .group-block-content", gallery: true },
    ];

    for (const cfg of configs) {
      const scroller = /** @type {HTMLElement | null} */ (document.querySelector(cfg.scroller));
      if (!scroller) continue;
      if (scroller.dataset.shadeScroll === 'ready') continue;
      scroller.dataset.shadeScroll = 'ready';

      // Progress bar: a track with a thumb whose width = viewport/content ratio
      // and whose x-offset tracks scrollLeft. Decorative (native scroll is the
      // real control), so aria-hidden. Width/transform are set by JS.
      const bar = document.createElement('div');
      bar.className = 'shade-scrollbar' + (cfg.gallery ? ' shade-scrollbar--gallery' : '');
      bar.setAttribute('aria-hidden', 'true');
      const thumb = document.createElement('div');
      thumb.className = 'shade-scrollbar__thumb';
      bar.appendChild(thumb);

      const mountTarget = cfg.gallery ? scroller : document.querySelector(cfg.mount);
      if (!mountTarget) continue;
      mountTarget.insertAdjacentElement('afterend', bar);

      // Cache the bar width so update() never reads layout after writing styles
      // (that interleave forces a synchronous reflow on every scroll tick).
      // bar width only changes on resize, tracked by a ResizeObserver.
      let barWidth = bar.clientWidth;
      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver((entries) => {
          barWidth = entries[entries.length - 1].contentRect.width;
        });
        ro.observe(bar);
        this.#resizeObservers.push(ro);
      }

      // Gallery: the native scroll container is not focusable by default, so a
      // keyboard user cannot advance it. Make it a labelled region and map the
      // arrow keys to one slide of travel. (WCAG 2.2 SC 2.1.1.)
      const slides = cfg.gallery ? /** @type {HTMLElement[]} */ (Array.from(scroller.children)) : null;
      if (cfg.gallery && slides && slides.length > 0) {
        scroller.setAttribute('tabindex', '0');
        scroller.setAttribute('role', 'region');
        scroller.setAttribute('aria-label', 'Field gallery, use the arrow keys to browse');
        const onKey = (e) => {
          // Reduced-motion users get an instant jump, not a smooth scroll.
          const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
          const step = scroller.clientWidth * 0.78;
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            scroller.scrollBy({ left: step, behavior });
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            scroller.scrollBy({ left: -step, behavior });
          }
        };
        scroller.addEventListener('keydown', onKey);
        this.#scrollListeners.push({ el: scroller, type: 'keydown', fn: onKey });
      }

      let ticking = false;
      const update = () => {
        ticking = false;
        // All layout reads first, all style writes after (no interleave).
        const sw = scroller.scrollWidth;
        const cw = scroller.clientWidth;
        const scrollLeft = scroller.scrollLeft;
        if (sw <= cw + 2) {
          bar.style.display = 'none';
          return;
        }
        const ratio = cw / sw;
        const maxScroll = sw - cw;
        const pos = maxScroll > 0 ? scrollLeft / maxScroll : 0;
        const travel = barWidth * (1 - ratio);

        // Gallery active index from scrollLeft: the slide whose centre is closest
        // to the viewport centre. Computed from geometry (not an Intersection
        // Observer rooted on a padded scroller, which Firefox clips), so it stays
        // correct cross-browser and survives child reordering.
        let activeIdx = -1;
        if (slides) {
          const centre = scrollLeft + cw / 2;
          let bestDist = Infinity;
          for (let i = 0; i < slides.length; i += 1) {
            const sc = slides[i].offsetLeft + slides[i].offsetWidth / 2;
            const d = Math.abs(sc - centre);
            if (d < bestDist) {
              bestDist = d;
              activeIdx = i;
            }
          }
        }

        bar.style.display = '';
        thumb.style.width = `${(ratio * 100).toFixed(2)}%`;
        thumb.style.transform = `translate3d(${(pos * travel).toFixed(1)}px, 0, 0)`;
        if (slides && activeIdx !== -1) {
          for (let i = 0; i < slides.length; i += 1) {
            slides[i].dataset.galleryState = i === activeIdx ? 'active' : 'inactive';
          }
        }
      };
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      scroller.addEventListener('scroll', onScroll, { passive: true });
      this.#scrollListeners.push({ el: scroller, type: 'scroll', fn: onScroll });

      // The resource-list carousel only gains its scrollable width once it
      // enters the viewport (the slideshow component flips overflow on
      // in-viewport). Recompute the bar when it scrolls into frame.
      if ('IntersectionObserver' in window) {
        const visObs = new IntersectionObserver(
          () => requestAnimationFrame(update),
          { threshold: 0.01 }
        );
        visObs.observe(scroller);
        this.#observers.push(visObs);
      }
      requestAnimationFrame(update);
    }
  }

  /**
   * Desktop "Tested in the field" contact-sheet scrub. As the gallery row
   * passes through the viewport, an active frame advances left-to-right: the
   * active photo sits at full opacity, the rest dim (CSS, motion-gated), and a
   * mono "FRM 0X / 0N" counter overlays each frame. Desktop only (mobile already
   * has the scrollLeft-driven active state from #initMobileCarousels). One
   * passive rAF scroll loop; transform/opacity only; idempotent.
   */
  #initGalleryScrub() {
    if (!window.matchMedia('(min-width: 750px)').matches) return;
    const row = document.querySelector("[id$='__gallery'] .group-block .group-block-content");
    if (!row || row.dataset.shadeScrub === 'ready') return;
    const frames = /** @type {HTMLElement[]} */ (Array.from(row.children));
    if (frames.length === 0) return;
    row.dataset.shadeScrub = 'ready';

    const total = frames.length;
    frames.forEach((frame, i) => {
      const counter = document.createElement('span');
      counter.className = 'shade-frame-counter';
      counter.setAttribute('aria-hidden', 'true');
      counter.textContent = `FRM ${String(i + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      frame.appendChild(counter);
    });

    let ticking = false;
    const update = () => {
      ticking = false;
      const rect = row.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // 0 as the row's top reaches the viewport bottom, 1 as its bottom clears the top.
      const progress = Math.min(1, Math.max(0, (vh - rect.top) / (rect.height + vh)));
      const idx = Math.min(total - 1, Math.floor(progress * total));
      for (let i = 0; i < total; i += 1) {
        frames[i].dataset.galleryState = i === idx ? 'active' : 'inactive';
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    this.#scrollListeners.push({ el: window, type: 'scroll', fn: onScroll });
    requestAnimationFrame(update);
  }

  /**
   * Global top scroll-progress hairline. A 2px bar whose scaleX tracks how far
   * down the page you are: an instrument-meter read on a tall page. The bar
   * grows on scroll, which is motion, so it is gated behind reduced-motion.
   * Passive rAF scroll; cached max refreshed via ResizeObserver. Idempotent
   * (body-level; removed on teardown).
   */
  #initScrollProgress() {
    if (prefersReducedMotion()) return;
    if (document.querySelector('.shade-scroll-progress')) return;
    const bar = document.createElement('div');
    bar.className = 'shade-scroll-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);

    let max = document.documentElement.scrollHeight - window.innerHeight;
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => {
        max = document.documentElement.scrollHeight - window.innerHeight;
      });
      ro.observe(document.body);
      this.#resizeObservers.push(ro);
    }
    let ticking = false;
    const update = () => {
      ticking = false;
      const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      bar.style.transform = `scaleX(${p.toFixed(4)})`;
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    this.#scrollListeners.push({ el: window, type: 'scroll', fn: onScroll });
    requestAnimationFrame(update);
  }

  /**
   * Hero scroll cue: a 1px tick with a travelling segment + "SCROLL" caption in
   * the hero gutter that fades out once past the first viewport and never
   * returns. Solves the tall-hero "is there more?" gap. Motion-gated (only
   * called in the no-preference path). Appended to the hero so it scrolls away.
   */
  #initScrollCue() {
    const hero = document.querySelector('.hero-wrapper .hero') || document.querySelector('.hero');
    if (!hero || hero.querySelector('.shade-scroll-cue')) return;
    const cue = document.createElement('div');
    cue.className = 'shade-scroll-cue';
    cue.setAttribute('aria-hidden', 'true');
    cue.innerHTML = '<span class="shade-scroll-cue__line"></span><span class="shade-scroll-cue__label">SCROLL</span>';
    hero.appendChild(cue);

    let ticking = false;
    const update = () => {
      ticking = false;
      cue.dataset.hidden = window.scrollY > window.innerHeight * 0.4 ? 'true' : 'false';
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    this.#scrollListeners.push({ el: window, type: 'scroll', fn: onScroll });
  }

  /**
   * Set up one IntersectionObserver that fires once for the group, then
   * reveals all targets in order with a 70ms stagger.
   * @param {HTMLElement[]} targets
   */
  #observeGroup(targets) {
    if (targets.length === 0) return;

    // Tag each target with its stagger index before it enters view.
    targets.forEach((el, i) => {
      el.dataset.shadeReveal = 'idle';
      el.style.setProperty('--reveal-index', String(i));
    });

    // Observe the first element in the group as the trigger sentinel. When it
    // crosses into view, reveal all siblings in the group in sequence.
    const sentinel = targets[0];

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry || !entry.isIntersecting) return;

        observer.disconnect();
        this.#revealGroup(targets);
      },
      {
        // Fire when the sentinel is 10% visible. Low threshold so the reveal
        // starts just as the section scrolls into frame, not after.
        threshold: 0.1,
        // Negative bottom margin: fire a little early so cards are already
        // animating by the time the user's eye reaches them.
        rootMargin: '0px 0px -40px 0px',
      }
    );

    observer.observe(sentinel);
    this.#observers.push(observer);
  }

  /**
   * Reveal all targets in a group with a 70ms stagger via class toggle.
   * Yields to the main thread first to ensure the transition starts on a clean
   * frame (same pattern as shade-hero.js).
   * @param {HTMLElement[]} targets
   */
  async #revealGroup(targets) {
    await yieldToMainThread();

    for (const el of targets) {
      el.dataset.shadeReveal = 'active';
    }
  }
}

// Register the custom element once.
if (!customElements.get('shade-reveal')) {
  customElements.define('shade-reveal', ShadeReveal);
}

// Self-instantiate exactly one instance so no liquid section edit is needed.
// Idempotent: never inject a second tag.
if (!document.querySelector('shade-reveal')) {
  document.body.appendChild(document.createElement('shade-reveal'));
}
